"use client";

import Image from "next/image";
import { FaArrowLeft } from "react-icons/fa";
import { LinkButton } from "../Standard";
import Link from "next/link";
import { useSelectedLayoutSegment } from "next/navigation";
import { FaEye, FaYoutube } from "react-icons/fa6";
import { BiLinkExternal, BiMobileAlt, BiSolidImageAlt } from "react-icons/bi";
import { CountUp } from "@std/app/Client";
import { useClientMeta } from "@std/@/kooleyy/hooks/client";

export const HeaderSkeleton = () => (
  <div className="flex flex-col gap-y-8 gap-x-4 w-full">
    <div className="flex flex-row items-center justify-center md:justify-start gap-x-8 md:gap-x-8">
      <div className="w-32 h-32 rounded-2xl bg-gray-500 animate-pulse" />
      <div className="flex flex-col gap-y-3">
        <div className="h-8 w-64 bg-gray-500 rounded-full animate-pulse" />
        <div className="flex flex-col gap-y-2">
          <div className="h-6 w-32 bg-gray-500 rounded-full animate-pulse" />
          <div className="h-6 w-32 bg-gray-500 rounded-full animate-pulse" />
        </div>
      </div>
    </div>
    <div className="w-full h-4 bg-gray-500 rounded-full animate-pulse" />
  </div>
);

export const Header = ({ id }) => {
  const [client, clientPending] = useClientMeta({
    id: id,
  });

  if (clientPending) return <HeaderSkeleton />;

  if (client?.id) {
    console.log(client);
  }

  const {
    name,
    subscribers,
    videos,
    views,
    avatar,
    show,
    banner,
    oldestVideo,
    newestVideo,
  } = client;

  const totalVideos = client?.totals?.allContent;

  const subsInt = isNaN(parseInt(subscribers)) ? null : parseInt(subscribers);
  const { format } = new Intl.NumberFormat("en-GB", {
    notation: "compact",
    compactDisplay: "short",
  });

  return (
    <div className="flex flex-col gap-y-6 gap-x-4 w-full">
      <div className="flex flex-col gap-y-8 items-center lg:flex-row lg:justify-between lg:gap-x-4 ">
        <HeaderAAG
          id={id}
          name={name}
          avatar={avatar}
          subscribers={format(subsInt || 0)}
          videos={format(totalVideos)}
          hasVideos={videos ? true : false}
          oldestVideo={oldestVideo}
          newestVideo={newestVideo}
        />
        {banner && (
          <>
            <Image
              src={banner?.regular?.url}
              className="hidden lg:block rounded-2xl lg:w-[45%] xl:w-[65%] 2xl:w-[70%]"
              width={banner?.regular?.width || 2560}
              height={banner?.regular?.height || 424}
              alt={`${name}'s banner`}
            />
            <Image
              src={banner?.mobile?.url}
              className="lg:hidden rounded-2xl md:w-[65%]"
              width={banner?.mobile?.width || 2560}
              height={banner?.mobile?.height || 424}
              alt={`${name}'s banner`}
            />
          </>
        )}
        <div className="hidden">
          <WeContributed videos={videos} views={views} />
        </div>
      </div>
      <HeaderTabs id={id} show={show} />
      <div className="hidden w-full h-1 rounded-full bg-gray-300" />
    </div>
  );
};

export const EscapeHatch = ({ name, avatar }) => {
  return (
    <div className="flex flex-row items-center gap-x-4">
      <LinkButton href="/clients">
        <FaArrowLeft />
        Back
      </LinkButton>
      <span className="text-2xl font-medium">
        <span className="font-bold">{name}&apos;s</span> client overview
      </span>
    </div>
  );
};

export const AAGAvatar = ({ id, avatar, name }) => (
  <Link
    href={`https://youtube.com/channel/${id}`}
    target="_blank"
    rel="noreferer noopener"
    className="group relative hover:cursor-pointer rounded-2xl w-24 md:w-32"
  >
    <div className="text-2xl ease-in-out duration-300 opacity-0 group-hover:opacity-100 absolute flex flex-col items-center justify-center bg-opacity-50 w-full h-full bg-black rounded-xl">
      <BiLinkExternal />
    </div>
    <Image
      src={avatar || "#"}
      className="object-cover rounded-2xl"
      alt={`${name}'s avatar`}
      width={512}
      height={512}
    />
  </Link>
);

// AAG: At A Glance
export const HeaderAAG = ({
  id,
  name,
  avatar,
  videos,
  subscribers,
  hasVideos,
  oldestVideo,
  newestVideo,
}) => {
  const oldDate = new Date(oldestVideo?.timestamp).toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
  });
  const newDate = new Date(newestVideo?.timestamp).toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
  });
  return (
    <div className="flex flex-row items-center justify-center md:justify-start gap-x-8 md:gap-x-8">
      <AAGAvatar avatar={avatar} name={name} id={id} />
      <div className="flex flex-col gap-y-1">
        <Link
          href={`https://youtube.com/channel/${id}`}
          target="_blank"
          rel="noreferer noopener"
          className="text-4xl hover:text-gray-300 ease-in-out duration-300 "
        >
          {name || "?"}
        </Link>
        <div className="flex flex-col gap-y-0">
          <span className="text-2xl font-semibold">
            <b>{subscribers || "?"}</b>+ subscribers
          </span>
          <span className="text-2xl font-semibold">
            <b>{videos || "?"}</b> videos
          </span>
          {hasVideos && (
            <span className="text-xl font-medium">
              {oldDate}
              {newDate !== oldDate && (
                <>
                  {" - "}
                  {newestVideo?.recent ? <>Present</> : <>{newDate}</>}
                </>
              )}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export const WeContributed = ({ views, videos, shorts, thumbnails }) => {
  const { format } = new Intl.NumberFormat("gb", {
    notation: "compact",
    compactDisplay: "short",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  const { format: formatInt } = new Intl.NumberFormat("gb", {
    notation: "compact",
    compactDisplay: "short",
  });

  if (videos === 0 && shorts === 0 && thumbnails === 0) return null;

  return (
    <div className="flex flex-col gap-y-2 p-4 rounded-2xl bg-gray-900 text-center">
      <span className="text-2xl">We&apos;ve contributed..</span>
      <div className="flex flex-col items-center">
        <div className="flex flex-col gap-y-2 text-center">
          {videos > 0 && (
            <div className="flex flex-row items-center gap-x-4">
              <FaYoutube className="text-3xl max-w-8" />
              <span className="text-xl font-medium">
                <CountUp
                  start={0}
                  end={videos}
                  useEasing
                  duration={3}
                  delay={2}
                  className="font-bold"
                />{" "}
                video{videos > 1 && "s"}
              </span>
            </div>
          )}
          {shorts > 0 && (
            <div className="flex flex-row items-center gap-x-4">
              <BiMobileAlt className="text-3xl max-w-8" />
              <span className="text-xl font-medium">
                <CountUp
                  start={0}
                  end={shorts}
                  useEasing
                  duration={3}
                  delay={2}
                  className="font-bold"
                />{" "}
                short{shorts > 1 && "s"}
              </span>
            </div>
          )}
          {thumbnails > 0 && (
            <div className="flex flex-row items-center gap-x-4">
              <BiSolidImageAlt className="text-3xl max-w-8" />
              <span className="text-xl font-medium">
                <CountUp
                  start={0}
                  end={thumbnails}
                  useEasing
                  duration={3}
                  delay={2}
                  className="font-bold"
                />{" "}
                thumbnail{thumbnails > 1 && "s"}
              </span>
            </div>
          )}
          {views > 0 && (
            <div className="flex flex-row items-center gap-x-4">
              <FaEye className="text-3xl max-w-8" />
              <span className="text-xl font-medium">
                <CountUp
                  start={0}
                  end={views}
                  useEasing
                  duration={3}
                  delay={2}
                  className="font-bold"
                />{" "}
                views
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const VisitOnYouTube = ({ id, name, subscribers, avatar }) => {
  const { format } = new Intl.NumberFormat("gb", {
    notation: "compact",
    compactDisplay: "short",
  });

  return (
    <div className="flex flex-col gap-y-2 p-4 rounded-2xl bg-gray-900 text-center">
      <span className="text-2xl">Visit on YouTube</span>
      <Link
        href={`https://youtube.com/channel/${id}`}
        target="_blank"
        rel="noreferrer noopener"
        className="flex flex-row gap-x-4 items-center justify-center"
      >
        <div className="group relative w-12 h-12">
          <Image
            src={avatar}
            alt={`${name}'s avatar`}
            width={64}
            height={64}
            className="object-cover ease-in-out duration-300 rounded-2xl"
          />
          <div className="text-xl top-0 left-0 ease-in-out duration-300 opacity-0 group-hover:opacity-100 absolute flex flex-col items-center justify-center bg-opacity-50 w-full h-full bg-black rounded-xl">
            <BiLinkExternal />
          </div>
        </div>
        <div className="flex flex-col items-start text-S">
          <span className="text-lg hover:text-gray-300 ease-in-out duration-300">
            {name}
          </span>
          <span className="font-medium">
            <b>{format(subscribers || 0)}</b> subscribers
          </span>
        </div>
      </Link>
    </div>
  );
};

export const DescriptionBox = ({ description }) => {
  return (
    <div className="flex flex-col gap-y-2 p-4 rounded-2xl bg-gray-900 text-center">
      <span className="text-2xl">Description</span>
      <span className="text-left font-medium whitespace-pre-wrap break-wrap">
        {description?.slice(0, 512)}
        {description?.length >= 512 && "..."}
      </span>
    </div>
  );
};

export const SideBoxes = ({
  id,
  name,
  subscribers,
  avatar,
  videos,
  shorts,
  thumbnails,
  views,
  description,
}) => (
  <div className="flex flex-col gap-y-4 w-full">
    <WeContributed
      views={views}
      videos={videos}
      shorts={shorts}
      thumbnails={thumbnails}
    />
    <VisitOnYouTube
      id={id}
      name={name}
      subscribers={subscribers}
      avatar={avatar}
    />
    <DescriptionBox description={description} />
  </div>
);

export const Tab = ({ children, href, active }) => {
  const paths = useSelectedLayoutSegment();
  return (
    <div className="group flex flex-col gap-y-1">
      <Link
        href={href}
        className={`text-2xl md:px-8 ${
          active == paths
            ? "text-white"
            : "text-gray-400 group-hover:text-gray-300"
        }`}
      >
        {children}
      </Link>
      <div
        className={`w-full ${
          active === paths
            ? "bg-cyan-300 group-hover:bg-cyan-400"
            : "group-hover:bg-gray-300"
        } h-1 z-10 md:px-8 rounded-full md:rounded-none`}
      />
    </div>
  );
};

export const HeaderTabs = ({ id, show }) => {
  const videosCondition = show.includes("videos");
  const thumbnailsCondition = show.includes("thumbnails");
  const shortsCondition = show.includes("shorts");
  return (
    <>
      <div className="flex flex-row items-center gap-x-8 md:gap-x-0 overflow-scroll md:overflow-hidden">
        <Tab href={`/clients/${id}`} active={null}>
          Home
        </Tab>
        {videosCondition && (
          <Tab href={`/clients/${id}/videos`} active="videos">
            Videos
          </Tab>
        )}
        {thumbnailsCondition && (
          <Tab href={`/clients/${id}/thumbnails`} active="thumbnails">
            Thumbnails
          </Tab>
        )}
        {shortsCondition && (
          <Tab href={`/clients/${id}/shorts`} active="shorts">
            Shorts
          </Tab>
        )}
      </div>
      <div className={`hidden md:block w-full bg-gray-500 -mt-7 h-1`} />
    </>
  );
};
